import { nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { NotTranslated, T } from '../../../Translations';
import Breadcrumb from '../Breadcrumb';
import Loading from '../Loading';
import PageLayout from '../PageLayout';
import StatusChip from '../StatusChip';
import Stepper from '../Stepper';
import { ConnectionResource, vendorName } from '../Types';
import AttributesCheck from './AttributesCheck';
import ConfigureIdp from './ConfigureIdp';
import Manage from './Manage';
import { useConnectionQuery } from './useConnectionQuery';

const Body: React.FC<{ resource: ConnectionResource }> = observer(({ resource }) => {
  switch (resource.payload.status) {
    case 'awaiting_metadata':
    case 'verifying':
      return (
        <>
          <div className="mt-6">
            <Stepper current={2} />
          </div>
          <div className="mt-6">
            <ConfigureIdp resource={resource} />
          </div>
        </>
      );
    case 'attributes_check':
      return (
        <>
          <div className="mt-6">
            <Stepper current={3} />
          </div>
          <div className="mt-6">
            <AttributesCheck resource={resource} onDone={nothing()} />
          </div>
        </>
      );
    case 'active':
    case 'needs_attention':
      return (
        <div className="mt-6">
          <Manage resource={resource} />
        </div>
      );
  }
});

function Connection() {
  const { id } = useParams();
  const { data, isLoading, error } = useConnectionQuery(id || '');

  return (
    <PageLayout>
      {isLoading ? (
        <Loading />
      ) : error || !data ? (
        <p className="text-muted-foreground">
          <T kind="An error occurred, please refresh the page and try again" />
        </p>
      ) : (
        <>
          <Breadcrumb>
            <NotTranslated text={data.payload.displayName} />
          </Breadcrumb>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                <NotTranslated text={data.payload.displayName} />
              </h2>
              <p className="mt-1 text-muted-foreground">
                <T kind={vendorName(data.payload.vendor)} /> &middot;{' '}
                <span className="font-mono text-sm">{data.payload.providerName}</span>
              </p>
            </div>
            <StatusChip status={data.payload.status} />
          </div>
          <Body resource={data} />
        </>
      )}
    </PageLayout>
  );
}

export default observer(Connection);
